import React, { useEffect, useMemo, useState } from 'react';
import AnimatedTitle from '../AnimatedTitle';
import '../improvimania/HostScreen.css';
import '../improvimania/HostScreen/Lobby.css';
import '../guessing/guessing.css';
import './andscene.css';
import AudioPlayer from '../AudioPlayer';
import andSceneTheme from '../../sound/andscene/theme.mp3';

export default function AndSceneHost({
  socket,
  sessionId,
  players,
  ipAddress,
  serverType,
  onReturnToPartyPack,
  removePlayer,
  setForceRemove,
  isMusicMuted = false,
}) {
  const [state, setState] = useState({
    phase: 'lobby',
    round: 0,
    writingDeadline: null,
    writingPrompts: [],
    locationAssignments: {},
    starterAssignments: {},
    playerWritingStatus: {},
    linePool: [],
    scenesPublic: [],
    bracketMatches: [],
    pendingAdvancers: [],
    currentMatch: null,
    completedMatches: [],
    votes: {},
    scoreboard: [],
    winnerScene: null,
    roundSummary: null,
    votingLocked: false,
    voteReveal: null,
  });

  const [timeLeft, setTimeLeft] = useState(60);

  useEffect(() => {
    if (!socket) return undefined;
    const update = (incoming) => setState((prev) => ({ ...prev, ...incoming }));
    socket.on('andSceneState', update);
    socket.emit('andSceneSyncState', { sessionId });
    return () => socket.off('andSceneState', update);
  }, [socket, sessionId]);

  useEffect(() => {
    if (state.phase !== 'writing' || !state.writingDeadline) return undefined;
    const tick = () => {
      setTimeLeft(Math.max(0, Math.ceil((state.writingDeadline - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [state.phase, state.writingDeadline]);

  const playersById = useMemo(() => {
    const map = new Map();
    players.forEach((p) => map.set(p.socketId, p.name));
    return map;
  }, [players]);

  const isProductionServer = serverType === 'fayaz.fly.dev';
  const isThemePlaying = state.phase === 'lobby' || state.phase === 'writing';

  const awaitingScenes = useMemo(() => {
    if (state.phase !== 'editing') return [];
    const submitted = new Set(Object.keys(state.scenes || {}));
    return players.filter((p) => !submitted.has(p.socketId));
  }, [state.phase, state.scenes, players]);

  const { eligibleVoters, waitingVoters } = useMemo(() => {
    if (state.phase !== 'voting' || !state.currentMatch || state.votingLocked) {
      return { eligibleVoters: [], waitingVoters: [] };
    }
    const owners = new Set([
      state.currentMatch.a?.ownerId,
      state.currentMatch.b?.ownerId,
    ].filter(Boolean));
    const eligible = players.filter((p) => !owners.has(p.socketId));
    const waiting = eligible.filter((p) => !state.votes?.[p.socketId]);
    return { eligibleVoters: eligible, waitingVoters: waiting };
  }, [state.phase, state.currentMatch, state.votes, players, state.votingLocked]);

  const { writingDone, writingWaiting } = useMemo(() => {
    if (state.phase !== 'writing') return { writingDone: [], writingWaiting: [] };
    const done = [];
    const waiting = [];
    players.forEach((p) => {
      const status = state.playerWritingStatus?.[p.socketId];
      if (status?.done) done.push(p);
      else waiting.push(p);
    });
    return { writingDone: done, writingWaiting: waiting };
  }, [state.phase, state.playerWritingStatus, players]);

  const timerClass = timeLeft <= 10
    ? 'andscene-host-timer-danger'
    : timeLeft <= 20
      ? 'andscene-host-timer-warn'
      : 'andscene-host-timer-ok';

  const SceneCard = ({ scene, votes, highlight, showOwner, winnerSceneId }) => {
    const ownerName = playersById.get(scene.ownerId) || 'Unknown';
    const voteCount = Object.values(votes || {}).filter((v) => v === scene.id).length;
    const isWinner = !!(winnerSceneId && winnerSceneId === scene.id);

    const cardClasses = [
      'question-card',
      'andscene-host-script-card',
      highlight ? 'andscene-host-script-card-highlight' : '',
      isWinner ? 'andscene-host-script-card-winner' : '',
    ].filter(Boolean).join(' ');

    return (
      <div className={cardClasses}>
        <p className="andscene-host-script-location">📍 {scene.location}</p>
        {(scene.lines || []).length === 0 && (
          <p className="andscene-host-script-empty">(empty scene)</p>
        )}
        {(scene.lines || []).map((line) => (
          <p key={line.id} className="andscene-host-script-line">
            <strong className="andscene-host-script-speaker">{line.speakerName || 'Unknown'}:</strong> {line.text}
          </p>
        ))}
        <p className="andscene-host-script-end">🎬 AND SCENE</p>
        <p className="andscene-host-script-owner">{showOwner ? `by ${ownerName}` : 'by ???'}</p>
        {isWinner && <div className="andscene-host-medal">🥇</div>}
        {voteCount > 0 && (
          <div className="andscene-host-vote-pill">
            {voteCount} vote{voteCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <AudioPlayer audioSrc={andSceneTheme} isPlaying={isThemePlaying} isMuted={isMusicMuted} />
      <div className="title-bar">
        <div className="join-message">
          <h2>
            {isProductionServer
              ? 'Production selected - players choose Production on their devices, no room code needed.'
              : <> Join at <span className="red-text">Fayaz.One</span> in your browser! </>}
          </h2>
        </div>
        <div className="room-info">
          {!isProductionServer && <h2>Room Code: {ipAddress}</h2>}
          <h4>Session: {sessionId}</h4>
        </div>
      </div>

      {state.phase === 'lobby' && (
        <div className="lobby-container">
          <AnimatedTitle title="And Scene" />
          <div className="app-container">
            <div className="players-container">
              <h4>Players:</h4>
              {[...playersById.entries()].map(([id, name]) => (
                <button
                  key={id}
                  className="ghost-button"
                  onClick={() => { setForceRemove(true); removePlayer(id, true); }}
                >
                  {name}
                </button>
              ))}
            </div>
            <div className="settings">
              <h2 className="andscene-host-settings-title">Game Settings</h2>
              <p>Players write lines for 60 seconds, build scenes, then vote in a bracket.</p>
              <p>Win a match: <strong>100 pts</strong> · Tie: <strong>50 pts each</strong></p>
              <p>Line used in winning scene: <strong>+10 pts</strong></p>
              <p className="andscene-host-subtle">Minimum 3 players required.</p>
            </div>
          </div>
          <button
            className="start-game primary-button"
            onClick={() => socket.emit('andSceneStartGame', { sessionId })}
            disabled={players.length < 3}
          >
            {players.length < 3
              ? `Need ${3 - players.length} more player${players.length === 2 ? '' : 's'}`
              : `Start Round ${(state.round || 0) + 1}`}
          </button>
        </div>
      )}

      {state.phase !== 'lobby' && (
        <div className="guessing-shell">
          <div className="guessing-game-layout">
             {state.phase !== 'round-end' && (
                <div className="score-column glass-card">
                <h3>Round {state.round}</h3>
                <p className="andscene-host-phase-label">Phase: {state.phase}</p>
                <h3>Scores</h3>
                {[...(state.scoreboard || [])].sort((a, b) => b.points - a.points).map((entry) => (
                    <div key={entry.id} className="score-item">
                    <span>{entry.name}</span>
                    <strong>{entry.points}</strong>
                    </div>
                ))}
                </div>
            )}

            <div className="board-column glass-card andscene-host-board">
              {state.phase === 'writing' && (
                <div>
                  <div className="andscene-host-writing-top">
                    <h3 className="andscene-host-writing-title">Writing Phase</h3>
                    <span className={`andscene-host-timer ${timerClass}`}>{timeLeft}s</span>
                  </div>

                  <p className="andscene-host-subtle">This phase auto-advances when everyone is done or the timer ends.</p>

                  <div className="andscene-host-two-col">
                    <div className="andscene-host-col">
                      <h4 className="andscene-host-done-title">Done ({writingDone.length})</h4>
                      {writingDone.map((p) => (
                        <p key={p.socketId} className="andscene-host-name-done">{p.name}</p>
                      ))}
                    </div>
                    <div className="andscene-host-col">
                      <h4 className="andscene-host-wait-title">Writing ({writingWaiting.length})</h4>
                      {writingWaiting.map((p) => (
                        <p key={p.socketId} className="andscene-host-name-wait">{p.name}</p>
                      ))}
                    </div>
                  </div>

                  <p className="andscene-host-footer-note">
                    {(state.linePool || []).length} line{(state.linePool || []).length !== 1 ? 's' : ''} submitted so far
                  </p>
                </div>
              )}

              {state.phase === 'editing' && (
                <div>
                  <h3>Building Scenes</h3>
                  <p className="andscene-host-editing-copy">
                    Players are assembling scenes from {(state.linePool || []).length} lines.
                  </p>

                  {awaitingScenes.length > 0 ? (
                    <div>
                      <h4 className="andscene-host-wait-title">Waiting for:</h4>
                      {awaitingScenes.map((p) => (
                        <p key={p.socketId} className="andscene-host-name-done">{p.name}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="andscene-host-ready">All scenes submitted - advancing to voting...</p>
                  )}

                  <p className="andscene-host-ready-count">
                    {players.length - awaitingScenes.length} / {players.length} scenes ready
                  </p>
                </div>
              )}

              {state.phase === 'voting' && state.currentMatch && (
                <div>
                  {(() => {
                    const reveal = state.voteReveal;
                    const revealActive = !!(state.votingLocked && reveal?.active);
                    const shownMatch = revealActive ? reveal.completedMatch : state.currentMatch;
                    const winnerSceneId = revealActive ? reveal.completedMatch?.bracketWinner : null;
                    const showOwner = revealActive;

                    return (
                      <>
                        <div className="andscene-host-voting-top">
                          <h3 className="andscene-host-voting-title">Match {shownMatch?.id || state.currentMatch.id}</h3>
                          <span className="andscene-host-voting-count">
                            {revealActive
                              ? `${Object.keys(state.votes || {}).length} / ${Object.keys(state.votes || {}).length} votes`
                              : `${Object.keys(state.votes || {}).length} / ${eligibleVoters.length} votes`}
                          </span>
                        </div>

                        <div className="gameshow-board andscene-host-vs-row">
                          {[shownMatch?.a, shownMatch?.b].filter(Boolean).map((scene) => (
                            <SceneCard
                              key={scene.id}
                              scene={scene}
                              votes={state.votes}
                              highlight={false}
                              showOwner={showOwner}
                              winnerSceneId={winnerSceneId}
                            />
                          ))}
                        </div>

                        {!revealActive && waitingVoters.length > 0 ? (
                          <div>
                            <h4 className="andscene-host-wait-voters-title">Waiting for votes from:</h4>
                            {waitingVoters.map((p) => (
                              <span key={p.socketId} className="andscene-host-voter-chip">{p.name}</span>
                            ))}
                          </div>
                        ) : !revealActive ? (
                          <p className="andscene-host-all-votes-in">All votes in - resolving...</p>
                        ) : (
                          <div className="andscene-host-reveal-box">
                            <p className="andscene-host-reveal-title">
                              {reveal?.completedMatch?.tie ? 'Tie vote! Points split 50/50.' : 'Vote closed! Awarding points...'}
                            </p>
                            {(reveal?.pointAwards || []).length > 0 ? (
                              <div className="andscene-host-awards-row">
                                {reveal.pointAwards.map((award, idx) => (
                                  <span
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${award.playerId}-${award.reason}-${idx}`}
                                    className="andscene-host-award-chip"
                                  >
                                    +{award.points} {playersById.get(award.playerId) || 'Unknown'} ({award.reason})
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="andscene-host-no-awards">No points this matchup.</p>
                            )}
                          </div>
                        )}

                        <p className="andscene-host-voting-note">
                          {revealActive
                            ? 'Revealing result for 5 seconds before the next vote opens...'
                            : 'The bracket advances automatically after the last vote.'}
                        </p>

                        {(state.completedMatches || []).length > 0 && (
                          <div className="andscene-host-completed-wrap">
                            <h4 className="andscene-host-completed-title">Completed this round:</h4>
                            {state.completedMatches.map((m) => {
                              const winner = [m.a, m.b].find((s) => s && s.id === m.bracketWinner);
                              return (
                                <p key={m.id} className="andscene-host-completed-line">
                                  {m.id}: {m.tie ? 'Tie ->' : '->'} {playersById.get(winner?.ownerId) || '?'} advances
                                  {m.b && ` (${m.votesA}-${m.votesB})`}
                                </p>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {state.phase === 'round-end' && (
                <div>
                  <h2 className="andscene-host-round-title">Shows Over!</h2>

                  {state.roundSummary?.winnerScene && (
                    <div className="question-card andscene-host-winning-card">
                      <h3>Winning Scene</h3>
                      <p className="andscene-host-winning-location">📍 {state.roundSummary.winnerScene.location}</p>
                      {(state.roundSummary.winnerScene.lines || []).map((line) => (
                        <p key={line.id}>
                          <strong>{line.speakerName || 'Unknown'}:</strong> {line.text}
                        </p>
                      ))}
                      <p className="andscene-host-winning-end">🎬 AND SCENE</p>
                      <p className="andscene-host-winning-owner">
                        Written by {playersById.get(state.roundSummary.winnerScene.ownerId) || '?'}
                      </p>
                    </div>
                  )}

                  <h3>Final Scores</h3>
                  {[...(state.scoreboard || [])].sort((a, b) => b.points - a.points).map((entry, idx) => (
                    <div key={entry.id} className={`score-item ${idx === 0 ? 'andscene-host-score-final-top' : 'andscene-host-score-final'}`}>
                      <span>{idx === 0 ? '🥇 ' : ''}{entry.name}</span>
                      <strong>{entry.points} pts</strong>
                    </div>
                  ))}

                  <p className="andscene-host-rule-note">{state.roundSummary?.pointsRule}</p>

                  <div className="andscene-host-actions">
                    <button
                      className="ghost-button"
                      onClick={() => socket.emit('andSceneStartGame', { sessionId })}
                      disabled={players.length < 3}
                    >
                      Play Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
